import type { ReactElement } from 'react';

import { ensureDefined, invariant } from '@e-socna/utils';
import { RESOLVER_CONTENT_CLASSNAME } from '@e-socna/printer-contract';

import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { formatAmount } from '../../../utils/format-amount.util';
import { formatPercent } from '../../../utils/format-percent.util';

import type {
  ResolverParameters,
  ResolverResult,
} from '../types/resolvers.type';

import { useFullyConsolidatedServiceUsages } from './hooks/useFullyConsolidatedServiceUsages.hook';
import { buildFeesModel, feesLineLabelOf } from './utils/build-fees-model.util';
import type { GroupTotals } from './hooks/useFullyConsolidatedServiceUsages.hook';
import type { FeesLine } from './utils/build-fees-model.util';

type CurrenciesIdMap = ReturnType<
  typeof useReferencesContext
>['currenciesIdMap'];

// The mandatory groups before discount: the discount, the VAT and the total
// follow them.
const renderMandatoryLine = (
  { _id, group, totals }: Readonly<FeesLine>,
  currenciesIdMap: CurrenciesIdMap,
) => (
  <tr key={_id}>
    <td align="left">{feesLineLabelOf(group)}</td>
    <td align="right">
      {totals
        ? formatAmount(totals.undiscountedAmountExcludingVat, currenciesIdMap)
        : null}
    </td>
  </tr>
);

const renderOptionLine = (
  { _id, group, totals }: Readonly<FeesLine>,
  currenciesIdMap: CurrenciesIdMap,
) => (
  <tr key={_id}>
    <td align="left">{feesLineLabelOf(group)}</td>
    <td align="right">
      {totals
        ? formatAmount(totals.discountedAmountExcludingVat, currenciesIdMap)
        : null}
    </td>
    <td />
    <td align="right">
      {totals
        ? formatAmount(totals.discountedAmountIncludingVat, currenciesIdMap)
        : null}
    </td>
  </tr>
);

const renderTotal = (
  {
    discountAsAmount,
    discountAsPercentage,
    discountedAmountExcludingVat,
    discountedAmountIncludingVat,
    vatAsAmount,
    vatAsPercentage,
  }: GroupTotals,
  currenciesIdMap: CurrenciesIdMap,
) => (
  <>
    {discountAsAmount.value !== 0 ? (
      <tr>
        <td align="left">Remise</td>
        <td align="right">{formatAmount(discountAsAmount, currenciesIdMap)}</td>
        <td align="right">{formatPercent(discountAsPercentage)}</td>
      </tr>
    ) : null}
    <tr>
      <td align="left">TVA</td>
      <td align="right">{formatAmount(vatAsAmount, currenciesIdMap)}</td>
      <td align="right">{formatPercent(vatAsPercentage)}</td>
    </tr>
    <tr>
      <td align="left">
        <strong>Total</strong>
      </td>
      <td align="right">
        <strong>
          {formatAmount(discountedAmountExcludingVat, currenciesIdMap)}
        </strong>
      </td>
      <td />
      <td align="right">
        <strong>
          {formatAmount(discountedAmountIncludingVat, currenciesIdMap)}
        </strong>
      </td>
    </tr>
  </>
);

// The fees by mandatory group, then their total, then the options one by one
// (the client has not chosen them yet: they are left out of the total).
export const useDevisHonoraires = (
  parameters: ResolverParameters,
): ResolverResult<ReactElement> => {
  const { currenciesIdMap } = useReferencesContext();

  const fullyConsolidated = useFullyConsolidatedServiceUsages(parameters);

  if (!fullyConsolidated.isSuccess) {
    return {
      ...fullyConsolidated,
      data: undefined,
    };
  }

  const {
    serviceUsagesGroups,
    totalCalculation: { result },
  } = ensureDefined({
    serviceUsagesGroups: fullyConsolidated.data?.serviceUsagesGroups,
    totalCalculation: fullyConsolidated.data?.totalCalculation,
  });

  invariant(!!result, 'Total calculation fragment data is required');

  const { mandatoryLines, mandatoryTotal, optionLines } =
    buildFeesModel(serviceUsagesGroups);

  // Without option, the quotation total is the one of the mandatory groups,
  // global discount included.
  const total = optionLines.length ? mandatoryTotal : result;

  const content = (
    <div className={RESOLVER_CONTENT_CLASSNAME}>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
          overflowWrap: 'anywhere',
        }}
      >
        <thead>
          <tr>
            <th align="left" style={{ width: '45%' }}>
              Description
            </th>
            <th align="right" style={{ width: '20%' }}>
              Montant HT
            </th>
            <th align="right" style={{ width: '15%' }}>
              %
            </th>
            <th align="right" style={{ width: '20%' }}>
              Montant TTC
            </th>
          </tr>
        </thead>
        <tbody>
          {mandatoryLines.map((line) =>
            renderMandatoryLine(line, currenciesIdMap),
          )}
          {total ? renderTotal(total, currenciesIdMap) : null}
          {optionLines.length ? (
            <>
              <tr>
                <td align="left" colSpan={4}>
                  <strong>Options</strong>
                </td>
              </tr>
              {optionLines.map((line) =>
                renderOptionLine(line, currenciesIdMap),
              )}
            </>
          ) : null}
        </tbody>
      </table>
    </div>
  );

  return {
    data: { generated: content },
    error: null,
    isSuccess: true,
    isLoading: false,
  };
};
